import React, { useEffect, useMemo, useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import { useShallow } from 'zustand/react/shallow';

import { debounce, getShortName } from '@/shared/lib';
import { AmountNumberInput, Button, Skeleton, Typography } from '@/shared/ui';

import { useSendMoney } from '../../../../model/useSendMoney';
import { serializeFormToInput, serializeInnerFormToInput } from '../lib';
import { calcFeeMutation } from '../model';
import { AccountView } from './account';

export const SendMoneyAmount = () => {
  const [isLoadingFee, setIsLoadingFee] = useState(false);
  const [fee, setFee] = useState<string | null>(null);
  const [totalAmount, setTotalAmount] = useState<string | null>(null);
  const { setStep, setAmount, amount, data, updateFormData } = useSendMoney(
    useShallow((state) => state),
  );
  const { mutateAsync } = useMutation({
    mutationFn: calcFeeMutation,
  });

  const debounceChangeAmount = useMemo(
    () =>
      debounce(async (value: string) => {
        try {
          if (!value) {
            setFee(null);
            setTotalAmount(null);
            return;
          }
          let fee = '0';
          let totalAmount = data?.amount;
          if (data?.isInner) {
            const response = await mutateAsync(
              serializeInnerFormToInput({
                ...data,
                amount: value,
              }),
            );

            fee = response?.originalTotalFee;
            totalAmount = response?.originalTotalAmount;
          } else if (data) {
            const response = await mutateAsync(
              serializeFormToInput({
                ...data,
                amount: value,
              }),
            );
            fee = response?.originalTotalFee;
            totalAmount = response?.originalTotalAmount;
          }
          setIsLoadingFee(false);
          setFee(fee?.toString());
          setTotalAmount(totalAmount?.toString());
        } catch (error) {
          console.log('calculate fee error', error);
          setFee(null);
          setTotalAmount(null);
          setErrorText('Failed to calculate fee');
        } finally {
          setIsLoadingFee(false);
        }
      }, 450),
    [mutateAsync, data],
  );

  useEffect(() => {
    if (data?.amount) {
      setAmount(data.amount);
      debounceChangeAmount(data.amount);
    }
  }, [data]);

  const [errorText, setErrorText] = useState('');

  const handleChange = (value: string) => {
    setErrorText('');
    setAmount(value);
    debounceChangeAmount(value);
    setIsLoadingFee(true);
    // }
  };
  const handleContinue = () => {
    if (+totalAmount > 0) {
      if ((+data?.account?.value || 0) >= +totalAmount) {
        updateFormData({ amount, totalAmount, fee });
        setStep('review-details');
      } else {
        setErrorText('Exceeds balance');
      }
    } else {
      setErrorText('Field is required');
    }
  };
  return (
    <div className="relative min-h-[532px] w-full overflow-hidden">
      <div className="flex flex-col gap-y-1 py-1">
        <Typography className="text-center">
          {data?.isInner
            ? getShortName(data?.innerEmail || data?.innerPhone)
            : getShortName(data?.accountNumber)}
        </Typography>
        <Typography
          variant="14-r"
          color="text-base-100"
          className="text-center"
        >
          {data?.isInner
            ? getShortName(data?.companyName)
            : getShortName(
                data?.companyName ||
                  `${data?.firstName || ''} ${data?.lastName || ''}`,
              )}
        </Typography>
      </div>
      <div className="overflow-hidden pb-[129px] pt-[133px]">
        <AmountNumberInput
          prefix={data?.account?.symbol}
          value={amount ?? '0'}
          onValueChange={handleChange}
          isInvalid={Boolean(errorText)}
          className="mx-auto"
          size="large"
        />
        {errorText ? (
          <p className="text-center text-base-100 typography-14r">
            {errorText}
          </p>
        ) : isLoadingFee ? (
          <Skeleton className="mx-auto h-[14px] w-[100px]" />
        ) : (
          Boolean(amount && fee) && (
            <p className="text-center text-base-100 typography-14r">
              {`Fees: ${data?.account?.symbol} ${fee}`}
            </p>
          )
        )}
      </div>
      <div className="absolute bottom-0 flex w-full flex-col gap-1">
        <AccountView ticker={data?.account?.code} />
        <Button size="lg" loading={isLoadingFee} onClick={handleContinue}>
          Continue
        </Button>
      </div>
    </div>
  );
};
